import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Button,
  Divider,
  Avatar,
  Chip,
  useTheme,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Stack
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  Leaderboard as LeaderboardIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Star as StarIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';
import { useManagedLocations } from '../auth/useManagedLocations';
import { collection, query, where, getDocs, orderBy, Timestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { LoadingSpinner } from '../../shared/components';
import { format, parseISO, isValid, addWeeks, isBefore, subDays } from 'date-fns';
import { latestCountedActivityByClient, SUCCESS_UPDATE_INTERVAL_DAYS } from "../../shared/success/successRecords";

// Interface for client data from Firestore
interface ClientData {
  id: string;
  client_id: string;
  client_name: string;
  lead_coach_name: string;
  lead_coach_id: string;
  primary_location: string;
  collaborators: string;
  collaborating_coach_ids: string[];
  mobile_number: string;
  email: string;
  health_consult_date: string | null;
  first_session_date: string | null;
  visit_dates: string[];
  active_programme: boolean | string; // Update type to boolean | string
  latest_expiration_date: string | null;
  pre_health_consult_data_submitted: boolean | null;
  health_consult_data_submitted: boolean | null;
  client_intake_data_submitted: boolean | null;
  assessment_inbody_data_submitted: boolean | null;
  assessment_nutrition_data_submitted: boolean | null;
  assessment_metabolic_data_submitted: boolean | null;
  assessment_movement_data_submitted: boolean | null;
  assessment_training_data_submitted: boolean | null;
  report_status: string | null;
  conversion_status: boolean | null;
  nps_rating?: number; // Added NPS rating field
  _syncTimestamp: Timestamp;
}

// Define interface for staff data
interface StaffData {
  staff_id: number;
  staff_name: string;
  formatted_staff_name: string;
  primary_location: string;
  visit_count: number;
  last_updated: Timestamp;
}

// Update CoachData interface to match the new data structure
interface CoachData {
  id: string;
  coach_id: string | number;
  name: string;
  email: string;
  role: string;
  primary_location: string;
  clients: number;
  activeClients: number;
  clientsWithCollaborators: number; // Percentage of clients with multiple collaborators
  checkInCompletion: number;
  clientSatisfaction: number;
  npsRatingsCount: number;
  npsRatingsTotal: number;
  recentPerformance: number;
  visit_count?: number;
  conversion30d: number | null;
  conversion90d: number | null;
  conversion180d: number | null;
  successCompletion: number;
}

// Interface for team metrics
interface TeamMetrics {
  totalClients: number;
  activeClients: number;
  avgClientsWithCollaborators: number;
  avgCheckInCompletion: number;
  avgClientSatisfaction: number;
  location: string;
}

// Helper function to format staff name from "Lastname, Firstname" to "Firstname Lastname"
const formatStaffName = (name: string): string => {
  if (!name || !name.includes(',')) return name;
  
  const [lastName, firstName] = name.split(',').map(part => part.trim());
  return `${firstName} ${lastName}`;
};

const CoachCard: React.FC<{ coach: CoachData }> = ({ coach }) => {
  const theme = useTheme();
  
  /**
   * Returns display text for a conversion value (e.g. "45%" or "N/A").
   */
  const formatConversion = (val: number | null): string => {
    return val !== null ? `${(val * 100).toFixed(0)}%` : 'N/A';
  };

  /**
   * Returns background colour based on conversion percentage.
   * ≥ 65 %  → success (green)
   * 40–64 % → warning (yellow)
   * < 40 %  → error (red)
   * null     → white / neutral
   */
  const getConversionBg = (val: number | null): string => {
    if (val === null) return theme.palette.common.white;
    const pct = val * 100;
    if (pct >= 65) return theme.palette.success.main;
    if (pct >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  /**
   * Determines an accessible foreground colour for given background.
   */
  const getConversionFg = (val: number | null): string => {
    if (val === null) return theme.palette.text.primary;
    return theme.palette.common.white;
  };
  
  // Fetch avatar from users collection based on coach_id
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchAvatar = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('coach_id', '==', coach.coach_id));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const data = snap.docs[0].data() as any;
          if (isMounted && data.photoURL) setAvatarUrl(data.photoURL as string);
        }
      } catch (err) {
        console.error('Error fetching user avatar', err);
      }
    };
    fetchAvatar();
    return () => { isMounted = false; };
  }, [coach.coach_id]);

  // Conversion tile subcomponent
  const ConversionTile: React.FC<{ label: string; value: number | null }> = ({ label, value }) => (
    <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box
        sx={{
          minWidth: 60,
          px: 1.2,
          py: 0.6,
          borderRadius: 1.2,
          textAlign: 'center',
          backgroundColor: getConversionBg(value),
          color: getConversionFg(value),
          fontWeight: 600,
          boxShadow: value !== null ? 2 : 0,
        }}
      >
        {formatConversion(value)}
      </Box>
    </Stack>
  );
  
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        borderRadius: 3,
        background: theme.palette.mode === 'light'
          ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`
          : theme.palette.background.paper,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        boxShadow: 2,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header with Avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar src={avatarUrl ?? undefined} sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
            {coach.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom={false}>
              {coach.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {coach.role} • {coach.primary_location}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Total Clients
              </Typography>
              <Typography variant="h6">
                {coach.clients}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Active Clients
              </Typography>
              <Typography variant="h6">
                {coach.activeClients}
              </Typography>
            </Grid>
            
            {/* Clients with Collaborators - Visual Progress Bar */}
            <Grid item xs={12}>
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Clients with Collaborators
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {coach.clientsWithCollaborators}%
                </Typography>
              </Box>
              <Box sx={{ width: '100%', position: 'relative' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={coach.clientsWithCollaborators} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 5,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 
                        coach.clientsWithCollaborators > 70
                          ? theme.palette.success.main
                          : coach.clientsWithCollaborators > 40
                          ? theme.palette.primary.main
                          : theme.palette.warning.main,
                      borderRadius: 5,
                    }
                  }}
                />
              </Box>
            </Grid>
            

            
            {/* Client Satisfaction - Star Rating */}
            <Grid item xs={12}>
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Client Satisfaction
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {coach.clientSatisfaction.toFixed(1)}/5
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon 
                    key={star} 
                    sx={{ 
                      color: star <= Math.round(coach.clientSatisfaction) 
                        ? theme.palette.warning.main 
                        : theme.palette.grey[300],
                      fontSize: 20,
                      mr: 0.5
                    }} 
                  />
                ))}
              </Box>
            </Grid>
            
            {/* Client Success Completion */}
            <Grid item xs={12}>
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Success Completion
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {coach.successCompletion}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={coach.successCompletion}
                sx={{
                  height: 8,
                  borderRadius: 5,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor:
                      coach.successCompletion >= 65
                        ? theme.palette.success.main
                        : coach.successCompletion >= 40
                        ? theme.palette.warning.main
                        : theme.palette.error.main,
                    borderRadius: 5,
                  },
                }}
              />
            </Grid>
            
            {/* Ignition Conversion Rates */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                First Programme Conversion Rate
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}><ConversionTile label="30d" value={coach.conversion30d} /></Grid>
                <Grid item xs={4}><ConversionTile label="90d" value={coach.conversion90d} /></Grid>
                <Grid item xs={4}><ConversionTile label="180d" value={coach.conversion180d} /></Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={6} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Visit Count
              </Typography>
              <Typography variant="h6">
                {coach.visit_count || 0}
              </Typography>
            </Grid>
            
            <Grid item xs={6} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Performance
              </Typography>
              <Chip 
                label={`${coach.recentPerformance}%`} 
                color={
                  coach.recentPerformance > 80
                    ? 'success'
                    : coach.recentPerformance > 60
                    ? 'primary'
                    : 'warning'
                }
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
      <CardActions>
      </CardActions>
    </Card>
  );
};

const TeamPerformance: React.FC = () => {
  const theme = useTheme();
  const { userRole } = useAuth();
  const managedLocations = useManagedLocations();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<string>('');
  const [coaches, setCoaches] = useState<CoachData[]>([]);
  const [teamMetrics, setTeamMetrics] = useState<TeamMetrics | null>(null);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  
  // Fetch available locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        
        // Fetch locations from staffLocations collection
        const staffRef = collection(db, 'staffLocations');
        const staffSnapshot = await getDocs(staffRef);
        
        if (staffSnapshot.empty) {
          // Fallback to hardcoded locations if no staff found
          const defaultLocations = ['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow'];
          setAvailableLocations(defaultLocations);
          
          // Pre-select location based on user role
          if (userRole === 'MANAGER' && managedLocations.length > 0) {
            setLocation(managedLocations[0]);
          } else if (userRole === 'ADMIN' && defaultLocations.length > 0) {
            setLocation(defaultLocations[0]);
          }
        } else {
          // Extract unique locations from staff data
          const locations = new Set<string>();
          
          staffSnapshot.docs.forEach(doc => {
            const staffData = doc.data() as StaffData;
            if (staffData.primary_location) {
              locations.add(staffData.primary_location);
            }
          });
          
          const fetchedLocations = Array.from(locations);
          setAvailableLocations(fetchedLocations);
          
          // Pre-select location based on user role
          if (userRole === 'MANAGER' && managedLocations.length > 0) {
            setLocation(managedLocations[0]);
          } else if (userRole === 'ADMIN' && fetchedLocations.length > 0) {
            setLocation(fetchedLocations[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        setError('Failed to load locations. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLocations();
  }, [userRole, managedLocations]);

  const fetchTeamData = useCallback(async () => {
    if (!location) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch staff for the selected location from staffLocations collection
      const staffRef = collection(db, 'staffLocations');
      
      console.log('Fetching staff for location:', location);
      
      let staffQuery;
      let staffSnapshot;
      
      try {
        // Query all staff first
        staffQuery = query(staffRef);
        const allStaffSnapshot = await getDocs(staffQuery);
        console.log(`Found ${allStaffSnapshot.size} total staff members`);
        
        if (allStaffSnapshot.empty) {
          console.log('No staff found in the system');
          setCoaches([]);
          setTeamMetrics(null);
          setLoading(false);
          return;
        }
        
        // Log all staff and their locations to debug
        console.log("All staff members:");
        const staffMap = new Map<string, any>();
        
        allStaffSnapshot.docs.forEach(doc => {
          const data = doc.data() as StaffData;
          console.log(`Staff: ${data.formatted_staff_name || data.staff_name}, Location: ${data.primary_location || 'None'}, ID: ${data.staff_id}`);
          
          // Use staff_id as the unique key to deduplicate
          if (data.staff_id) {
            staffMap.set(data.staff_id.toString(), { doc, data });
          }
        });
        
        console.log(`After deduplication: ${staffMap.size} unique staff members`);
        
        // Filter by location manually - be more flexible with location matching
        const filteredByLocation = Array.from(staffMap.values())
          .filter(({ data }) => {
            // Check if staff has a primary_location that matches any part of our location
            return data.primary_location && 
                  (data.primary_location.toLowerCase().includes(location.toLowerCase()) ||
                   location.toLowerCase().includes(data.primary_location.toLowerCase()));
          })
          .map(({ doc }) => doc);
        
        console.log(`Found ${filteredByLocation.length} staff for location ${location} (flexible matching)`);
        
        // If no staff found with flexible matching, use all staff
        if (filteredByLocation.length === 0) {
          console.log('No staff found for this location, using all unique staff instead');
          staffSnapshot = {
            docs: Array.from(staffMap.values()).map(({ doc }) => doc),
            empty: staffMap.size === 0,
            size: staffMap.size
          };
        } else {
          // Create a new snapshot-like object with the filtered docs
          staffSnapshot = {
            docs: filteredByLocation,
            empty: filteredByLocation.length === 0,
            size: filteredByLocation.length
          };
        }
        
      } catch (error) {
        console.error('Error fetching staff:', error);
        setError('Failed to load team data. Please try again.');
        setLoading(false);
        return;
      }
      
      if (staffSnapshot.empty) {
        console.log('No staff found for location:', location);
        setCoaches([]);
        setTeamMetrics(null);
        setLoading(false);
        return;
      }
      
      // Process each staff member to get their performance metrics
      const coachesWithMetrics = await Promise.all(
        staffSnapshot.docs.map(async (staffDoc) => {
          const staffData = staffDoc.data() as StaffData;
          const staffId = staffData.staff_id.toString();
          
          // Fetch clients for this staff member
          const clientsRef = collection(db, 'bigquery_cache/client_data/comprehensive_details');
          console.log(`Fetching clients for staff: ${staffData.formatted_staff_name || staffData.staff_name} (${staffId})`);
          
          // Try to fetch lead coach clients
          let leadCoachClients: ClientData[] = [];
          try {
            // Support both string and numeric lead_coach_id values
            const leadCoachQuery = query(
              clientsRef,
              where('lead_coach_id', 'in', [staffId, Number(staffId)])
            );
            const leadCoachSnapshot = await getDocs(leadCoachQuery);
            console.log(`Found ${leadCoachSnapshot.size} lead coach clients`);
            
            if (leadCoachSnapshot.size > 0) {
              // Log sample client data to debug
              const sampleClient = leadCoachSnapshot.docs[0].data();
              console.log('Sample client data fields:', Object.keys(sampleClient));
            }
            
            leadCoachClients = leadCoachSnapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data() 
            } as ClientData));
          } catch (error) {
            console.error('Error fetching lead coach clients:', error);
          }
          
          // Try to fetch collaborator clients
          let collaboratorClients: ClientData[] = [];
          try {
            // First try string id in array
            let collaboratorSnapshot = await getDocs(
              query(clientsRef, where('collaborating_coach_ids', 'array-contains', staffId))
            );
            // If none found, try numeric id variant
            if (collaboratorSnapshot.empty) {
              const numericId = Number(staffId);
              if (!Number.isNaN(numericId)) {
                collaboratorSnapshot = await getDocs(
                  query(clientsRef, where('collaborating_coach_ids', 'array-contains', numericId))
                );
              }
            }
            console.log(`Found ${collaboratorSnapshot.size} collaborator clients`);
            
            collaboratorClients = collaboratorSnapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data() 
            } as ClientData));
          } catch (error) {
            console.error('Error fetching collaborator clients:', error);
          }
          
          // Get all clients for this staff member
          const allClients: ClientData[] = [
            ...leadCoachClients
          ];
          
          console.log(`Total clients for ${staffData.formatted_staff_name || staffData.staff_name}: ${allClients.length}`);
          
          // Calculate metrics
          const totalClients = allClients.length;
          const activeClients = allClients.filter(client => {
            if (typeof client.active_programme === 'boolean') {
              return client.active_programme === true;
            } else if (typeof client.active_programme === 'string') {
              return client.active_programme === 'true';
            }
            return false;
          }).length;
          
          // Calculate clients with collaborators
          const clientsWithCollaborators = allClients.filter(client => client.collaborating_coach_ids && client.collaborating_coach_ids.length > 1).length;
          const clientsWithCollaboratorsPercentage = totalClients > 0 ? (clientsWithCollaborators / totalClients) * 100 : 0;
          
          // Calculate check-in completion rate
          let checkInCompletion = 0;
          let totalRequiredCheckIns = 0;
          let completedCheckIns = 0;
          
          allClients.forEach(client => {
            // Count completed check-ins based on submitted data
            let clientCompletedCheckIns = 0;
            
            // Check various assessment submissions
            if (client.pre_health_consult_data_submitted) clientCompletedCheckIns++;
            if (client.health_consult_data_submitted) clientCompletedCheckIns++;
            if (client.client_intake_data_submitted) clientCompletedCheckIns++;
            if (client.assessment_inbody_data_submitted) clientCompletedCheckIns++;
            if (client.assessment_nutrition_data_submitted) clientCompletedCheckIns++;
            if (client.assessment_metabolic_data_submitted) clientCompletedCheckIns++;
            if (client.assessment_movement_data_submitted) clientCompletedCheckIns++;
            if (client.assessment_training_data_submitted) clientCompletedCheckIns++;
            
            completedCheckIns += clientCompletedCheckIns;
            
            // Calculate required check-ins based on client status
            // Active clients should have all check-ins, inactive may have fewer
            const requiredCheckIns = client.active_programme ? 8 : 4;
            totalRequiredCheckIns += requiredCheckIns;
          });
          
          checkInCompletion = totalRequiredCheckIns > 0 
            ? (completedCheckIns / totalRequiredCheckIns) * 100 
            : 0;
          
          // Calculate client satisfaction (NPS rating average) using LEAD coach clients only
          let clientSatisfactionSum = 0;
          let clientsWithRatings = 0;
          
          leadCoachClients.forEach(client => {
            if (client.nps_rating && !isNaN(Number(client.nps_rating))) {
              clientSatisfactionSum += Number(client.nps_rating);
              clientsWithRatings++;
            }
          });
          
          const clientSatisfaction = clientsWithRatings > 0 
            ? clientSatisfactionSum / clientsWithRatings 
            : 0;
          
          // Client Success Completion Calculation
          let successCompleted = 0;
          let successOutstanding = 0;
          let activeSuccessClientCount = 0;

          const sixWeeksAgo = subDays(new Date(), SUCCESS_UPDATE_INTERVAL_DAYS);

          // Pre-fetch latest success update per client (expensive but acceptable per staff)
          const latestSuccessMap = new Map<string, Date>();
          for (const client of allClients) {
            try {
              const successQ = query(
                collection(db, 'client_success'),
                where('client_id', '==', client.client_id),
                orderBy('created_at', 'desc')
              );
              const successSnap = await getDocs(successQ);
              const latestCounted = latestCountedActivityByClient(
                successSnap.docs.map(d => ({ id: d.id, data: d.data() }))
              ).get(String(client.client_id));
              if (latestCounted) {
                latestSuccessMap.set(client.client_id, latestCounted);
              }
            } catch (err) {
              console.error('Error fetching client_success for', client.client_id, err);
            }
          }

          allClients.forEach((client) => {
            // Determine if client has active programme
            const isActive = typeof client.active_programme === 'boolean'
              ? client.active_programme
              : client.active_programme === 'true';

            if (!isActive) {
              // Skip inactive clients entirely; success update not required
              return;
            }

            activeSuccessClientCount++;

            // Determine first session date
            let firstSession: Date | null = null;
            if (client.first_session_date) {
              firstSession = new Date(client.first_session_date);
            } else if (client.visit_dates && client.visit_dates.length > 0) {
              const sorted = [...client.visit_dates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
              firstSession = new Date(sorted[0]);
            }

            const latestSuccess = latestSuccessMap.get(client.client_id);
            let needsSuccessUpdate = false;

            if (latestSuccess) {
              needsSuccessUpdate = latestSuccess < sixWeeksAgo && client.active_programme === true;
            } else if (firstSession && client.active_programme === true) {
              needsSuccessUpdate = firstSession < sixWeeksAgo;
            }

            if (needsSuccessUpdate) successOutstanding++; else successCompleted++;
          });

          const successCompletion = activeSuccessClientCount > 0 ? (successCompleted / activeSuccessClientCount) * 100 : 0;

          // Fetch ignition conversion rates
          let conversion30d: number | null = null;
          let conversion90d: number | null = null;
          let conversion180d: number | null = null;
          try {
            const ignitionColRef = collection(db, 'ignition_conversion');
            // Attempt 1: Direct document lookup (doc id === staffId)
            const directSnap = await getDoc(doc(ignitionColRef, staffId));
            let ignitionData: any | null = null;
            if (directSnap.exists()) {
              ignitionData = directSnap.data();
            } else {
              // Attempt 2: Query by staff_id field (handles auto-ID docs)
              const q = query(ignitionColRef, where('staff_id', '==', Number(staffId)));
              const qSnap = await getDocs(q);
              if (!qSnap.empty) {
                ignitionData = qSnap.docs[0].data();
              }
            }
            if (ignitionData && ignitionData.conversion_rate) {
              conversion30d = ignitionData.conversion_rate['30_days'] ?? null;
              conversion90d = ignitionData.conversion_rate['90_days'] ?? null;
              conversion180d = ignitionData.conversion_rate['180_days'] ?? null;
            }
          } catch (error) {
            console.error('Error fetching ignition conversion data:', error);
          }
          
          // Calculate recent performance (weighted average of the metrics)
          const recentPerformance = (
            clientsWithCollaboratorsPercentage * 0.6 + 
            (clientSatisfaction * 20) * 0.4
          );
          
          // Round values for display
          const roundedClientsWithCollaborators = Math.round(clientsWithCollaboratorsPercentage);
          const roundedCheckInCompletion = Math.round(checkInCompletion);
          const roundedClientSatisfaction = Number(clientSatisfaction.toFixed(1));
          const roundedRecentPerformance = Math.round(recentPerformance);
          const roundedSuccessCompletion = Math.round(successCompletion);
          
          return {
            id: staffDoc.id,
            coach_id: staffId,
            name: formatStaffName(staffData.formatted_staff_name || staffData.staff_name),
            email: '', // Email not available in staff data
            role: 'Staff', // Role not available in staff data
            primary_location: staffData.primary_location || location,
            clients: totalClients,
            activeClients,
            clientsWithCollaborators: roundedClientsWithCollaborators,
            checkInCompletion: roundedCheckInCompletion,
            clientSatisfaction: roundedClientSatisfaction,
            npsRatingsCount: clientsWithRatings,
            npsRatingsTotal: clientSatisfactionSum,
            recentPerformance: roundedRecentPerformance,
            visit_count: staffData.visit_count,
            conversion30d,
            conversion90d,
            conversion180d,
            successCompletion: roundedSuccessCompletion
          };
        })
      );
      
      setCoaches(coachesWithMetrics);
      
      // Calculate team metrics
      if (coachesWithMetrics.length > 0) {
        const totalClients = coachesWithMetrics.reduce((sum, coach) => sum + coach.clients, 0);
        const activeClients = coachesWithMetrics.reduce((sum, coach) => sum + coach.activeClients, 0);
        const avgClientsWithCollaborators = coachesWithMetrics.reduce((sum, coach) => sum + coach.clientsWithCollaborators, 0) / coachesWithMetrics.length;
        const avgCheckInCompletion = coachesWithMetrics.reduce((sum, coach) => sum + coach.checkInCompletion, 0) / coachesWithMetrics.length;
        const totalNpsCount = coachesWithMetrics.reduce((sum, coach) => sum + coach.npsRatingsCount, 0);
        const totalNpsSum = coachesWithMetrics.reduce((sum, coach) => sum + coach.npsRatingsTotal, 0);
        const avgClientSatisfaction = totalNpsCount > 0 ? totalNpsSum / totalNpsCount : 0;
        
        setTeamMetrics({
          totalClients,
          activeClients,
          avgClientsWithCollaborators,
          avgCheckInCompletion,
          avgClientSatisfaction,
          location
        });
      } else {
        setTeamMetrics(null);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      setError('Failed to load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [location]);

  // Fetch team data when location changes
  useEffect(() => {
    fetchTeamData();
  }, [location, fetchTeamData]);

  const handleLocationChange = (event: SelectChangeEvent) => {
    setLocation(event.target.value);
  };

  if (userRole === 'USER') {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You do not have permission to view team performance data. This page is only accessible to managers and administrators.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Team Performance
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="location-label">Location</InputLabel>
              <Select
                labelId="location-label"
                id="location"
                value={location}
                label="Location"
                onChange={handleLocationChange}
                disabled={userRole === 'MANAGER' && managedLocations.length < 2}
              >
                {availableLocations.map((loc) => (
                  <MenuItem 
                    key={loc} 
                    value={loc}
                    disabled={userRole === 'MANAGER' && !managedLocations.includes(loc)}
                  >
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Divider sx={{ mb: 3 }} />
        
        {loading ? (
          <LoadingSpinner minHeight="300px" />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : !teamMetrics ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            No team data available for the selected location.
          </Alert>
        ) : (
          <Box>
            <Typography variant="h5" gutterBottom>
              {teamMetrics.location} Team Overview
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        bgcolor: 'primary.main', 
                        borderRadius: '50%', 
                        p: 1, 
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PeopleIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" component="div">
                        {teamMetrics.totalClients}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Clients
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        bgcolor: 'success.main', 
                        borderRadius: '50%', 
                        p: 1, 
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PersonAddIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" component="div">
                        {teamMetrics.activeClients}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Clients
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ 
                          bgcolor: 'info.main', 
                          borderRadius: '50%', 
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <GroupIcon sx={{ fontSize: 24, color: 'white' }} />
                        </Box>
                        <Typography variant="h5" component="div">
                          {teamMetrics.avgClientsWithCollaborators.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Clients with Collaborators
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={teamMetrics.avgClientsWithCollaborators} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 5,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: 
                              teamMetrics.avgClientsWithCollaborators > 70
                                ? 'success.main'
                                : teamMetrics.avgClientsWithCollaborators > 40
                                ? 'primary.main'
                                : 'warning.main',
                            borderRadius: 5,
                          }
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ 
                          bgcolor: 'warning.main', 
                          borderRadius: '50%', 
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <StarIcon sx={{ fontSize: 24, color: 'white' }} />
                        </Box>
                        <Typography variant="h5" component="div">
                          {teamMetrics.avgClientSatisfaction.toFixed(1)}/5
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Client Satisfaction
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <StarIcon 
                            key={star} 
                            sx={{ 
                              color: star <= Math.round(teamMetrics.avgClientSatisfaction) 
                                ? 'warning.main' 
                                : 'grey.300',
                              fontSize: 24
                            }} 
                          />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Coach Performance
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {coaches.map((coach) => (
                <Grid item key={coach.id} xs={12} sm={6} md={4}>
                  <CoachCard coach={coach} />
                </Grid>
              ))}
            </Grid>
            
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Performance Insights
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Collaboration Analysis
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {teamMetrics?.avgClientsWithCollaborators > 50 ? (
                        <>
                          <strong>Strong collaboration:</strong> {teamMetrics?.avgClientsWithCollaborators.toFixed(1)}% of clients have multiple collaborators, 
                          indicating effective team-based coaching. This collaborative approach typically leads to better client outcomes and satisfaction.
                        </>
                      ) : teamMetrics?.avgClientsWithCollaborators > 30 ? (
                        <>
                          <strong>Moderate collaboration:</strong> {teamMetrics?.avgClientsWithCollaborators.toFixed(1)}% of clients have multiple collaborators. 
                          There's opportunity to increase cross-coach collaboration for improved client support.
                        </>
                      ) : (
                        <>
                          <strong>Limited collaboration:</strong> Only {teamMetrics?.avgClientsWithCollaborators.toFixed(1)}% of clients have multiple collaborators. 
                          Consider promoting more team-based coaching to enhance client experience and results.
                        </>
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Recommendation: {teamMetrics?.avgClientsWithCollaborators < 40 ? 
                        "Implement regular case review sessions where coaches can identify opportunities to collaborate on client programs." : 
                        "Continue fostering the collaborative environment and document successful multi-coach approaches for training."}
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card elevation={1} sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Top Performers
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      {coaches
                        .sort((a, b) => b.recentPerformance - a.recentPerformance)
                        .slice(0, 3)
                        .map((coach, index) => (
                          <Box key={coach.id} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: ['gold', 'silver', '#cd7f32'][index], mr: 2 }}>
                              {index + 1}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body1">{coach.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{coach.role}</Typography>
                            </Box>
                            <Chip 
                              label={`${coach.recentPerformance}%`} 
                              color="success" 
                              size="small" 
                            />
                          </Box>
                        ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TeamPerformance;
